<template>
  <ExpandableCard v-model:expanded="isExpanded" class="remove-orders-list-item">
    <div class="top-row row">
      <div class="top-row-left flex flex-gap-1 align-items-center">
        <Checkbox
          :model-value="selected"
          :input-id="`remove-orders-list-item-${order.id}-checkbox`"
          :aria-label="t('removeOrders.selectThisOrder')"
          binary
          @update:model-value="emit('update:selected', $event)"
        />

        <div class="order-number font-medium text-gray-darkest">
          {{ t('orders.orderNumParam', { param: order.id }) }}
        </div>

        <OrderChip :status="order.status" />
      </div>

      <div class="top-row-right measurement">
        <FontAwesomeIcon
          aria-hidden="true"
          class="text-gray-darker"
          :icon="['fas', 'weight-hanging']"
        />
        {{
          translateNumberedEntity(
            totalWeight,
            'uoms.weight.abbreviations.POUND',
          )
        }}
      </div>
    </div>

    <div class="second-row row">
      <div class="second-row-left flex flex-gap-1 align-items-center">
        <span>
          {{ order?.origin?.location?.cityState }}
        </span>
        <i class="pi pi-arrow-right text-gray-dark"></i>
        <span>
          {{ order?.destination?.location?.cityState }}
        </span>
      </div>

      <div class="second-row-right measurement">
        <FontAwesomeIcon
          aria-hidden="true"
          class="text-gray-darker"
          :icon="['fas', 'list']"
        />
        {{ totalHandlingUnits }}
        {{ handlingUnits }}
      </div>
    </div>

    <template #expansion>
      <div class="line-items">
        <LineItemViewEdit
          v-for="(lineItem, index) in order.lineItems"
          :key="index"
          :model-value="lineItem"
          :index="index"
          :editable="false"
          class="line-item"
        />
      </div>
    </template>
  </ExpandableCard>
</template>

<script lang="ts" setup>
import { ref, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateNumberedEntity } from '@rygen/rygen-ui/i18n';

import { useLineItemsSummary } from '@/composables';
import { Order } from '@/model';

import LineItemViewEdit from '@/components/lineItem/LineItemViewEdit.vue';
import OrderChip from '@/components/order/OrderChip.vue';
import ExpandableCard from '@rygen/rygen-ui/components/ui/ExpandableCard.vue';
import Checkbox from 'primevue/checkbox';

const props = defineProps({
  order: {
    type: Order,
    default: () => new Order(),
  },
  selected: {
    type: Boolean,
    default: () => false,
  },
});
const { order, selected } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:selected', newVal: boolean): void;
}>();

const lineItems = toRef(() => order.value.lineItems);

const { t } = useI18n();
const { handlingUnits, totalHandlingUnits, totalWeight } =
  useLineItemsSummary(lineItems);

const isExpanded = ref(false);
</script>

<style>
.remove-orders-list-item {
  .second-row-left {
    /* checkbox width + gap */
    padding-left: calc(var(input-height) + var(--sp-3));
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--sp-3);

    .measurement {
      /* between w-1 and w-2 */
      width: 12.5%;

      .svg-inline--fa {
        margin-right: var(--sp-1);
      }
    }
  }

  .expandable-card-expansion {
    .line-items {
      display: flex;
      flex-flow: column;
      gap: var(--sp-3);
      width: 95%;
      margin-left: auto;
    }
  }
}
</style>
